import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  schema,
}: SEOHeadProps) {
  const fullTitle = `${title} - 韩漫网`;
  const siteUrl = 'http://kefaul.cn/';
  const fullCanonical = canonicalUrl.startsWith('http') ? canonicalUrl : `${siteUrl}${canonicalUrl}`;

  const setMetaTag = (name: string, content: string, isProperty = false) => {
    const attr = isProperty ? 'property' : 'name';
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  const setLinkTag = (rel: string, href: string) => {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', rel);
      document.head.appendChild(el);
    }
    el.setAttribute('href', href);
  };

  const removeExistingSchema = () => {
    document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => el.remove());
  };

  const setSchema = () => {
    removeExistingSchema();
    if (schema) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema, null, 0);
      document.head.appendChild(script);
    }
  };

  useEffect(() => {
    document.title = fullTitle;
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);
    setLinkTag('canonical', fullCanonical);
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', fullCanonical, true);
    setMetaTag('og:site_name', '韩漫网', true);
    setMetaTag('og:locale', 'zh_CN', true);
    if (ogImage) {
      setMetaTag('og:image', ogImage, true);
    }
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', description);
    if (ogImage) {
      setMetaTag('twitter:image', ogImage);
    }
    setSchema();
  }, [description, fullCanonical, fullTitle, keywords, ogImage, ogType, schema]);

  return null;
}

export function siteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫网',
    url: 'http://kefaul.cn/',
    description: '韩漫网汇集最新热门韩漫推荐和最火排行榜，大全作品免费阅读，在线观看无限制，下拉式设计让您轻松探索多样内容。',
    inLanguage: 'zh-CN',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'http://kefaul.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
}

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫网',
    url: 'http://kefaul.cn/',
    logo: 'http://kefaul.cn/logo.png',
    description: '韩漫网 - 最新热门韩国漫画推荐与排行榜平台',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9999',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '望京SOHO T3-A座 2508室',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      postalCode: '100102',
      addressCountry: 'CN',
    },
  };
}

export function breadcrumbSchema(items: { name: string; url?: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url || undefined,
    })),
  };
}

export function articleSchema(article: {
  title: string;
  description: string;
  image: string;
  url: string;
  author: string;
  datePublished: string;
  dateModified: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    url: article.url,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    publisher: {
      '@type': 'Organization',
      name: '韩漫网',
      logo: {
        '@type': 'ImageObject',
        url: 'http://kefaul.cn/logo.png',
      },
    },
  };
}

export function faqSchema(questions: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  };
}

export function webPageSchema(page: { title: string; description: string; url: string }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: page.title,
    description: page.description,
    url: page.url,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: 'http://kefaul.cn/',
    },
  };
}
